import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Modal,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import config from '../config';

const server_URL = config.SERVER_URL;

// Available modules (excluding dashboard, customers, and reports)
const AVAILABLE_MODULES = [
  { id: 'custom-fields', name: 'Custom Fields' },
  { id: 'list', name: 'Lists' },
  { id: 'tags', name: 'Tags' },
  { id: 'members', name: 'Contacts' },
  { id: 'segments', name: 'Segments' },
  { id: 'servers', name: 'Servers' },
  { id: 'links', name: 'Links' },
  { id: 'template', name: 'Templates' },
  { id: 'campaigns', name: 'Campaigns' },
  { id: 'trigger-automations', name: 'Trigger Automations' },
  { id: 'events', name: 'Events' },
  { id: 'customer-api', name: 'API' },
  { id: 'api-logs', name: 'API Logs' },
  { id: 'activity-logs', name: 'Activity Logs' },
  { id: 'email-logs', name: 'Emails Logs' },
];

const SubUsers = () => {
  const [openModal, setOpenModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [subusers, setSubusers] = useState([]);
  const [allSubusers, setAllSubusers] = useState([]); // Store all subusers for filtering
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingSubuser, setEditingSubuser] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('');
  const [userRole, setUserRole] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    full_name: '',
    email: '',
    password: '',
    modules: [],
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    fetchSubUsers();
    fetchCustomers();
  }, []);

  // Filter subusers based on customer filter
  useEffect(() => {
    if (customerFilter === '' || customerFilter === null) {
      setSubusers(allSubusers);
    } else {
      const filtered = allSubusers.filter(
        (subuser) => subuser.customer_id === parseInt(customerFilter)
      );
      setSubusers(filtered);
    }
  }, [customerFilter, allSubusers]);

  const fetchCustomers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/subusers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'get_customers',
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSubUsers = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/subusers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subusers');
      }

      const data = await response.json();
      console.log('Received subuser data:', data);
      
      if (data.status === '1' && data.data) {
        const subusersData = Array.isArray(data.data) ? data.data : [];
        setAllSubusers(subusersData);
        // The useEffect will handle filtering
      } else {
        setAllSubusers([]);
        setSubusers([]);
      }
    } catch (error) {
      console.error('Error fetching subusers:', error);
      setSubusers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      customer_id: '',
      full_name: '',
      email: '',
      password: '',
      modules: [],
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      customer_id: '',
      full_name: '',
      email: '',
      password: '',
      modules: [],
    });
  };

  const handleOpenEditModal = (subuser) => {
    setEditingSubuser(subuser);
    setFormData({
      customer_id: subuser.customer_id,
      full_name: subuser.full_name,
      email: subuser.email,
      password: '',
      modules: subuser.modules || [],
    });
    setEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setEditingSubuser(null);
    setFormData({
      customer_id: '',
      full_name: '',
      email: '',
      password: '',
      modules: [],
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModuleChange = (moduleId) => {
    setFormData((prev) => {
      const modules = prev.modules || [];
      if (modules.includes(moduleId)) {
        return {
          ...prev,
          modules: modules.filter((id) => id !== moduleId),
        };
      } else {
        return {
          ...prev,
          modules: [...modules, moduleId],
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/subusers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'add',
          customer_id: formData.customer_id,
          full_name: formData.full_name,
          email: formData.email,
          password: formData.password,
          modules: formData.modules,
          created_by: userData.id,
          user_id: userData.id,
          role_id: userData.role_id,
        }),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchSubUsers();
        handleCloseModal();
        alert('Subuser added successfully');
      } else {
        alert(data.error || 'Failed to add subuser');
      }
    } catch (error) {
      console.error('Error adding subuser:', error);
      alert('An error occurred while adding subuser');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const requestBody = {
        method: 'update',
        id: editingSubuser.id,
        customer_id: formData.customer_id,
        full_name: formData.full_name,
        email: formData.email,
        modules: formData.modules,
        updated_by: userData.id,
        user_id: userData.id,
        role_id: userData.role_id,
      };

      // Only include password if it's provided
      if (formData.password) {
        requestBody.password = formData.password;
      }

      const response = await fetch(`${server_URL}/api/subusers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      
      if (data.status === '1') {
        await fetchSubUsers();
        handleCloseEditModal();
        alert('Subuser updated successfully');
      } else {
        alert(data.error || 'Failed to update subuser');
      }
    } catch (error) {
      console.error('Error updating subuser:', error);
      alert('An error occurred while updating subuser');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subuser?')) {
      return;
    }

    setDeletingId(id);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${server_URL}/api/subusers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'delete',
          id: id,
          deleted_by: userData.id,
          user_id: userData.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to delete subuser');
      }

      const data = await response.json();
      if (data.status === '1') {
        await fetchSubUsers();
        alert('Subuser deleted successfully');
      } else {
        alert(data.error || 'Failed to delete subuser');
      }
    } catch (error) {
      console.error('Error deleting subuser:', error);
      alert('Failed to delete subuser');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Users
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenModal}
        >
          Add User
        </Button>
      </Box>

      {/* Customer Filter - Only show if user role is 1 */}
      {userRole === 1 && customers.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Filter by Customer"
            >
              <MenuItem value="">
                <em>All Customers</em>
              </MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.username} ({customer.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <Typography sx={{ mt: 2 }}>Loading subusers...</Typography>
          </Box>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                {userRole === 1 && <TableCell>Customer</TableCell>}
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Modules</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subusers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={userRole === 1 ? 6 : 5} align="center">
                    <Typography>No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                subusers.map((subuser, index) => (
                  <TableRow 
                    key={subuser.id}
                    hover
                  >
                    <TableCell>{index + 1}</TableCell>
                    {userRole === 1 && <TableCell>{subuser.customer_name}</TableCell>}
                    <TableCell>{subuser.full_name}</TableCell>
                    <TableCell>{subuser.email}</TableCell>
                    <TableCell>
                      {subuser.modules && subuser.modules.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {subuser.modules.map((moduleId) => {
                            const module = AVAILABLE_MODULES.find((m) => m.id === moduleId);
                            return module ? (
                              <Box
                                key={moduleId}
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  bgcolor: 'primary.light',
                                  color: 'primary.contrastText',
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                }}
                              >
                                {module.name}
                              </Box>
                            ) : null;
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No modules
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleOpenEditModal(subuser)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(subuser.id)}
                        disabled={deletingId === subuser.id}
                      >
                        {deletingId === subuser.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          'Delete'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Sub User Modal */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-subuser-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="add-subuser-modal-title" variant="h6" component="h2" mb={3}>
            Add User
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  label="Customer"
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Full Name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Modules (All modules except Dashboard)
                </Typography>
                <FormGroup>
                  {AVAILABLE_MODULES.map((module) => (
                    <FormControlLabel
                      key={module.id}
                      control={
                        <Checkbox
                          checked={formData.modules.includes(module.id)}
                          onChange={() => handleModuleChange(module.id)}
                        />
                      }
                      label={module.name}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Adding...
                    </>
                  ) : (
                    'Add User'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>

      {/* Edit Sub User Modal */}
      <Modal
        open={editModal}
        onClose={handleCloseEditModal}
        aria-labelledby="edit-subuser-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <Typography id="edit-subuser-modal-title" variant="h6" component="h2" mb={3}>
            Edit User
          </Typography>
          <form onSubmit={handleUpdate}>
            <Stack spacing={3}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  label="Customer"
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.username} ({customer.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Full Name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                required
              />
              <TextField
                label="Password (leave blank to keep current)"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                helperText="Leave blank to keep current password"
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Modules (All modules except Dashboard)
                </Typography>
                <FormGroup>
                  {AVAILABLE_MODULES.map((module) => (
                    <FormControlLabel
                      key={module.id}
                      control={
                        <Checkbox
                          checked={formData.modules.includes(module.id)}
                          onChange={() => handleModuleChange(module.id)}
                        />
                      }
                      label={module.name}
                    />
                  ))}
                </FormGroup>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleCloseEditModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update User'
                  )}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default SubUsers;
